import * as React from 'react';
import {
	Modal,
	Button,
	Grid,
	XFilledIcon
} from 'react-unity';
import '../../../../components/PortOpeningRequests/components/porRule/RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import ToggleField from '../../../common/form-controls/ToggleField';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './OnPremAbstractRulesForm';
import { RuleAction } from '../../../../models/enums/POR/RuleAction';
import SelectField from '../../../common/form-controls/SelectField';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import { PortOpeningRequestsDirection } from '../../../../models/enums/POR/PortOpeningRequestsDirection';

interface RuleModalSubToOnPremProps extends RulesFormProps{
	porDirection: PortOpeningRequestsDirection;
}

interface SubToOnPremRuleStateForm extends IRuleStateForm {
	name: string;
    //trafficDirection: TrafficDirection;
	sourceIPs: string,
	destinationURLs: string;
    protocol: string;
}
interface RuleModalSubToOnPremState {
	stateForm: SubToOnPremRuleStateForm;
}

export default class RuleModalSubToOnPrem extends AbstractRulesForm<
	RuleModalSubToOnPremProps,
	RuleModalSubToOnPremState
> {

	constructor(props: RuleModalSubToOnPremProps) {
		super(props);
		this.state = {
			stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
		};

		this.initFormModel();
	}

	isURLValid = (url) => /^(((http(s)?:\/\/.)?(www\.)?)|(\*\.)?)[a-zA-Z0-9]+[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(url);

	initFormModel() {
		super.initFormModel();
		this.formModel.addField('name', {
			getValue: () => this.state.stateForm.name,
			validation: {
				required: true,
				rules:
					this.state.stateForm.operation?.name === RuleOperation.Add.name &&
					[
						{
							assert: () => this.state.stateForm.name?.length <= 74,
							message: 'Name cannot be longer than 74 characters.',
						},
						{
							assert: () => !this.state.stateForm.name?.includes(' '),
							message: 'Name cannot contain spaces.',
						},
						{
							assert: () => /^[a-zA-Z](?:[a-zA-Z0-9_.-]*[a-zA-Z0-9])?$/.test(this.state.stateForm.name ?? ''),
							message: 'Name must begin with a letter, end with a letter or number, and may contain only letters, numbers, underscores, periods or dashes.',
						}
					]
			}
		});

		this.formModel.addField('sourceIPs', {
			getValue: () => this.state.stateForm.sourceIPs,
			validation: {
				required : ()=> !this.IsProxyVerification(this.props.porDirection?.name),
				rules: [
					{
						assert: () => !this.hasEmptyValues(this.state.stateForm.sourceIPs),
						message: 'Empty values are not allowed.'
					}
				]
			}
		});

		this.formModel.fields.sourceIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false
			})
		);

		this.formModel.fields.sourceIPs.validation.required =
			() => !this.IsProxyVerification(this.props.porDirection?.name)

		this.formModel.fields.destinationIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false
			})
		);

		this.formModel.fields.destinationIPs.validation.required =
			() => !(this.state.stateForm.destinationURLs?.trim().length > 0);

		this.formModel.addField('protocol', {
			getValue: () => this.state.stateForm.protocol,
			validation: {
				required: true
			},
		});

		this.formModel.fields.destinationPorts.validation.rules.push(
			this.PortInputValidationRule({
				minimumPort: 0
			})
		);

		this.formModel.addField('description', {
			getValue: () => this.state.stateForm.description
		});

		const numberOfDestinationItemsValidation = {
			assert: () => {
				const numberOfIPs = this.state.stateForm.destinationIPs?.split(';').filter(s => s !== '').length ?? 0;
				const numberOfURLs = this.state.stateForm.destinationURLs?.split(';').filter(s => s !== '').length ?? 0;
				const numberOfDestinationItems = numberOfIPs + numberOfURLs;
				const maxNumberOfDestinationItems = 50;
				return numberOfDestinationItems <= maxNumberOfDestinationItems;
			},
			message: "Total Destination URLs and IPs can't be greater than 50"
		}

		this.formModel.addField('destinationURLs', {
			getValue: () => this.parseMultipleValues(this.state.stateForm.destinationURLs),
			validation: {
				required: () => !(this.state.stateForm.destinationIPs?.trim().length > 0),
				rules: [
					{
						assert: () => !this.hasEmptyValues(this.state.stateForm.destinationURLs),
						message: 'Empty values are not allowed.'
					},
					{
						assert: () => !(this.state.stateForm.destinationURLs?.includes('https://')
						||this.state.stateForm.destinationURLs?.includes('http://')),
						message: 'URL cannot have "http" or "https"'
					},
					{
						assert: (value) => {
							const urls = this.parseMultipleValues(value)?.split(';');
							return urls?.every(url => this.isURLValid(url));
						},
						message: (value) => {
							if (value !== undefined){
							const invalidAddress = this.parseMultipleValues(value)?.split(';').find(url => !this.isURLValid(url));
							return `${invalidAddress} is an invalid URL.`;}
							else return 'Empty values are not allowed.'
						}
					},
					numberOfDestinationItemsValidation
				]
			}
		});
	}
	
	// eslint-disable-next-line class-methods-use-this
	getDefaultFormState() {
		return {
			operation: RuleOperation.Add,
			action: RuleAction.Allow,
			sslBypass: 'false',
			name: '',
			protocol: ''
		};
	}

	resetIPsValidations(){
		this.formModel.fields.sourceIPs.validation.rules = [];
		this.formModel.fields.destinationIPs.validation.rules = [];
	}

	wasEditted() {
		if(this.props.editingRule){
			return (
				this.props.editingRule.description != this.stateFormHandler().description.value				
				|| this.props.editingRule.action.value != this.stateFormHandler().action.value			
				|| this.props.editingRule.sslBypass != this.stateFormHandler().sslBypass.value			
				|| this.props.editingRule.sourceIPs != this.state.stateForm.sourceIPs
				|| this.props.editingRule.destinationIPs != this.stateFormHandler().destinationIPs.value
				|| this.props.editingRule.destinationPorts != this.stateFormHandler().destinationPorts.value
				|| this.props.editingRule.destinationURLs != this.state.stateForm.destinationURLs
				|| this.props.editingRule.protocol != this.state.stateForm.protocol
				|| this.props.editingRule.operation != this.state.stateForm.operation
		)}
		return true
	}

	render() {
		return (
			<Modal show={this.props.visible}>
				<Modal.Window className="ruleModal">
					<Modal.Window.Header>
						<Modal.Window.Header.Title>							
							{this.props.isReadOnly ? 'View Rule' : 'New Rule'}
						</Modal.Window.Header.Title>
						<Modal.Window.Header.CloseButton onClick={this.props.onClose}>
							<XFilledIcon size='small' />
						</Modal.Window.Header.CloseButton>
					</Modal.Window.Header>
					<Modal.Window.Body>
						{ this.isModalForNewRule() &&
							<ToggleField
								{...this.stateFormHandler().operation}								
								label="Operation"
								disabled={!this.formCanBeEdited()}
								options={
									[
										RuleOperation.Add,//new RuleOperation().fromValue(this.stateFormHandler().operation.value),
										RuleOperation.Remove
									]	}								
									
								
							/>
						}
						<Grid variant="4-up">
									<Grid.Item>
										<ToggleField											
											value={'true'}
											options= { [
												{ text: 'Permanent', value: 'true' }
												]}
											label="Type"
											disabled={true}
										/>
									</Grid.Item>									
						</Grid>
						<Grid variant="2-up">
							<Grid.Item>	
								<TextInputField
									label="Rule Name"
									value={this.state.stateForm.name}
									validation={this.formModel.fields.name.validation}
									onChange={
										(event) => {
											this.handleStateFormChange('name', event.target.value);
										}
									}
									disabled={(this.state.stateForm.operation?.name !== RuleOperation.Add.name && this.state.stateForm.operation?.name !== RuleOperation.Remove.name) || !this.formCanBeEdited()}
								/>
							</Grid.Item>
						</Grid>
							<>
								<Grid variant="2-up">
									<Grid.Item>
										<ToggleField
											{...this.stateFormHandler().action}
											options= { (this.formCanBeEdited())? [
												RuleAction.Allow,
												RuleAction.Block,
											]:this.stateFormHandler().action?.value?
											[RuleAction.Allow]
											:
											[RuleAction.Block]
											}
											label="Action"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>									
								</Grid>
								<Grid variant="2-up">
								{this.IsProxyVerification(this.props.porDirection?.name) && 
								<Grid.Item>								
								<ToggleField
									{...this.stateFormHandler().sslBypass}
									options={ 
									[
										{ value: 'true', text: 'Yes' },
										{ value: 'false', text: 'No' },
									]}
									value={this.stateFormHandler().sslBypass?.value.toString()}
									label="SSL Bypass"
									disabled={!this.formCanBeEdited()}
								/>									
								</Grid.Item>}									
								</Grid>
								<Grid variant="2-up">
									{!this.IsProxyVerification(this.props.porDirection?.name) && 
									<Grid.Item>
										<TextAreaField
											value={this.state.stateForm.sourceIPs}
											label="Source (IPs / IP Range / CIDR)"
											validation={this.formModel.fields.sourceIPs.validation}
											onChange={
												(event) => {
													this.handleStateFormChange('sourceIPs', event.target.value);
												}
											}
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>}
									<Grid.Item>
										<TextAreaField
											{...this.stateFormHandler().destinationIPs}
											label="Destination (IPs / IP Range / CIDR)"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
								</Grid>
								<Grid variant="2-up">
									<Grid.Item>
										<TextAreaField
											{...this.stateFormHandler().destinationPorts}
											label="Destination Ports"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
									<Grid.Item>
										<TextAreaField
											label="Destination (URLs)"
											value={this.state.stateForm.destinationURLs}
											validation={this.formModel.fields.destinationURLs.validation}
											onChange={
												(event) => {
													this.handleStateFormChange('destinationURLs', event.target.value);
												}
											}
											note='Multiple values are allowed, separated by semicolons.'
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
								</Grid>
								<Grid variant="2-up">
									<Grid.Item>
										<TextAreaField
											{...this.stateFormHandler().description}
											label="Description"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
									<Grid.Item>
										<SelectField
											label="Protocol"
											value={this.state.stateForm.protocol}
											options={['Any', 'TCP', 'UDP', 'ICMP']}
											onChange={(event) => this.handleStateFormChange('protocol', event.target.value)}
											validation={this.formModel.fields.protocol.validation}
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
								</Grid>
							</>
					</Modal.Window.Body>
					{ !this.props.isReadOnly &&
						<Modal.Window.Footer>
							<Grid variant="2-up">
								<Grid.Item>
									<Button
										variant="primary"
										disabled={(!this.formModel.isValid() || !this.wasEditted())}
										onClick={this.handleRequest}
									>
										{this.props.editingRule ? 'Confirm' : 'Create'}
									</Button>
								</Grid.Item>
								<Grid.Item>
									<Button
										variant="secondary"
										onClick={this.handleCancel}
									>
										Cancel
									</Button>
								</Grid.Item>
							</Grid>
						</Modal.Window.Footer>
					}
				</Modal.Window>
			</Modal>
		);
	}
}